import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Grid,
  GridItem,
  Input,
  useDisclosure,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { FC, useState } from "react";

import { RanaModal } from "@nens/ranalib";
import { Simulation, Template } from "@nens/threedi/openapi-client";

import { getSimulationTemplatesApi } from "../../apiConfiguration";

import styles from "./SaveAsSimulationTemplateButton.module.css";

interface SaveAsSimulationTemplateButtonProps {
  simulation: Simulation;
}

const SaveAsSimulationTemplateButton: FC<SaveAsSimulationTemplateButtonProps> = ({
  simulation,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const simulationTemplatesApi = getSimulationTemplatesApi();
  const [simulationTemplateName, setSimulationTemplateName] = useState("");
  const [includeEvents, setIncludeEvents] = useState(true);
  const [includeInitials, setIncludeInitials] = useState(true);
  const [includeSettings, setIncludeSettings] = useState(true);

  const close = () => {
    saveAsTemplateMutation.reset();
    onClose();
  };

  const saveAsTemplateMutation = useMutation<Template, { status: string; statusText: string }>({
    mutationFn: async () => {
      return await simulationTemplatesApi.simulationTemplatesCreate({
        data: {
          simulation: simulation.id + "",
          name: simulationTemplateName,
          includeInitials: includeInitials,
          includeEvents: includeEvents,
          includeSettings: includeSettings,
        },
      });
    },
  });

  const confirmDisabledText =
    simulationTemplateName === ""
      ? "Please name the template"
      : !includeEvents && !includeInitials && !includeSettings
      ? "Select at least one option"
      : "";

  const disableForm = saveAsTemplateMutation.isSuccess || saveAsTemplateMutation.isPending;
  return (
    <>
      <Button variant="secondary" onClick={onOpen}>
        Save as template
      </Button>
      <RanaModal
        isOpen={isOpen}
        onClose={close}
        topBar="Create simulation template from simulation"
      >
        <p>
          Stores this simulation as a template, so that new simulations can be run with the same
          settings.
        </p>
        <Box marginTop={"4"}>
          <fieldset disabled={disableForm} className={styles.Fieldset}>
            <Grid>
              <GridItem>
                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto auto",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Template name</span>
                  <Input
                    type="text"
                    placeholder={""}
                    value={simulationTemplateName}
                    onChange={(event) => setSimulationTemplateName(event.target.value)}
                    minWidth={"260px"}
                    padding={"6px"}
                  />
                </label>
              </GridItem>
            </Grid>
            <Grid>
              <GridItem>
                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto auto",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Include events</span>
                  <Checkbox
                    name={undefined}
                    isChecked={includeEvents}
                    onChange={() => setIncludeEvents(!includeEvents)}
                    title={"Include events"}
                    isDisabled={disableForm}
                  />
                </label>
              </GridItem>
              <GridItem>
                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto auto",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Include initials</span>
                  <Checkbox
                    name={undefined}
                    isChecked={includeInitials}
                    onChange={() => setIncludeInitials(!includeInitials)}
                    title={"Include initials"}
                    isDisabled={disableForm}
                  />
                </label>
              </GridItem>
              <GridItem>
                <label
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto auto",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Include settings</span>
                  <Checkbox
                    name={undefined}
                    isChecked={includeSettings}
                    onChange={() => setIncludeSettings(!includeSettings)}
                    title={"Include settings"}
                    isDisabled={disableForm}
                  />
                </label>
              </GridItem>
            </Grid>
          </fieldset>
        </Box>
        <Box my={4}>
          {saveAsTemplateMutation.isSuccess ? (
            <Alert status="success" flexDirection={"column"} marginBottom={"4"}>
              <AlertIcon />
              <AlertTitle>Saved as template</AlertTitle>
              <AlertDescription>
                Saving as template was succesfull. You may close the dialog
              </AlertDescription>
            </Alert>
          ) : saveAsTemplateMutation.isError ? (
            <Alert status="error" flexDirection={"column"} marginBottom={"4"}>
              <AlertIcon />
              <AlertTitle>Failed saving as template</AlertTitle>
              <AlertDescription>
                Saving as template failed. Close or try again.
                {"Status:" + (saveAsTemplateMutation.error && saveAsTemplateMutation.error.status)}
                {saveAsTemplateMutation.error && saveAsTemplateMutation.error.statusText}
              </AlertDescription>
            </Alert>
          ) : null}
          <Grid
            gridAutoColumns={"min-content min-content"}
            justifyContent={"space-between"}
            gridAutoFlow={"column"}
            width={"100%"}
          >
            {saveAsTemplateMutation.isSuccess ? (
              <Button onClick={close}>Close</Button>
            ) : (
              <>
                <Button onClick={close}>Cancel</Button>
                <Button
                  variant="primary"
                  title={confirmDisabledText !== "" ? confirmDisabledText : "save as template"}
                  disabled={confirmDisabledText !== "" || disableForm}
                  onClick={async () => {
                    saveAsTemplateMutation.mutate();
                  }}
                >
                  Confirm
                </Button>
              </>
            )}
          </Grid>
        </Box>
      </RanaModal>
    </>
  );
};

export default SaveAsSimulationTemplateButton;
